import { Link } from 'react-router-dom'

import type { AdvertisingField, CompetitionAdvertising, DetailUnavailableReason, PromotingCampaign } from '@/api/competitionDetail'
import { Badge, EmptyState, formatCurrency, formatRatio, type FormatOptions } from '@/design'
import { drillDownHref } from '@/lib/drilldown'
import type { GlobalFilters } from '@/lib/filters'

import { formatDay } from '../../list/cells'
import { wordLabel } from '../format'
import { Group, TabRead, Tile, TileGroup } from '../pieces'
import { useCompetitionAdvertising } from '../queries'
import { describeDetailReason, UNDEFINED_FIGURE } from '../reasons'

/**
 * The Suite campaigns promoting the competition whose dates touch the period
 * (`…/advertising`), read when the tab opens, each with its channels. Spend, attributed
 * revenue, CPA and ROAS are never given per competition — a campaign can promote
 * several, and an even split would be a number made up — so they read "Not live yet"
 * with that reason; the Advertising and Channel performance pages report them per
 * campaign and channel.
 */
export function AdvertisingTab({ competitionId, filters, money }: { competitionId: string; filters: GlobalFilters; money: FormatOptions }) {
  const read = useCompetitionAdvertising(competitionId, filters)
  return (
    <TabRead read={read} loading="Loading advertising">
      {(advertising) => <AdvertisingBody advertising={advertising} filters={filters} money={{ ...money, currency: advertising.currency }} />}
    </TabRead>
  )
}

/** A spend figure's reason in a few words, under its tile, when the group gives it in full. */
const BRIEF: Partial<Record<DetailUnavailableReason, string>> = {
  no_spend_per_competition: 'No spend per competition',
  needs_ad_platform: 'Needs a connected ad platform',
}

function AdvertisingBody({
  advertising,
  filters,
  money,
}: {
  advertising: CompetitionAdvertising
  filters: GlobalFilters
  money: FormatOptions
}) {
  const { notAvailable } = advertising
  // Every spend figure is missing for one reason: said once, in full, under the heading.
  const reasons = new Set(Object.values(notAvailable))
  const shared = reasons.size === 1 ? [...reasons][0] : undefined
  const amount = (field: AdvertisingField, value: number | null, show: (value: number) => string) => {
    if (value !== null) return { value: show(value) }
    const reason = notAvailable[field] ?? null
    return { reason, value: UNDEFINED_FIGURE, note: reason && reason === shared ? BRIEF[reason] : undefined }
  }
  const currency = (value: number) => formatCurrency(value, money)

  return (
    <div className="cd-stack">
      <TileGroup title="Spend and return" lead={shared ? describeDetailReason(shared).why : undefined}>
        <Tile label="Ad spend" {...amount('adSpend', advertising.adSpend, currency)} />
        <Tile label="Attributed revenue" {...amount('attributedRevenue', advertising.attributedRevenue, currency)} />
        <Tile label="CPA" {...amount('cpa', advertising.cpa, currency)} />
        <Tile label="ROAS" {...amount('roas', advertising.roas, formatRatio)} />
      </TileGroup>
      <Group title="Campaigns promoting this competition">
        {advertising.campaigns.length === 0 ? (
          <EmptyState
            variant="no-results"
            compact
            title="No campaigns in this period"
            description="No campaign in Competition Insights names this competition with dates that touch this period."
          />
        ) : (
          <ul className="cd-campaigns">
            {advertising.campaigns.map((campaign) => (
              <CampaignCard key={campaign.campaignId} campaign={campaign} filters={filters} timezone={advertising.timezone} locale={money.locale} />
            ))}
          </ul>
        )}
      </Group>
    </div>
  )
}

function CampaignCard({
  campaign,
  filters,
  timezone,
  locale,
}: {
  campaign: PromotingCampaign
  filters: GlobalFilters
  timezone: string
  locale: string | undefined
}) {
  const dates = (startsAt: string | null, endsAt: string | null) => {
    const day = (instant: string | null) => (instant ? formatDay(instant, timezone, locale) : 'open')
    return startsAt === null && endsAt === null ? 'No dates' : `${day(startsAt)} – ${day(endsAt)}`
  }
  const href = drillDownHref({ path: `/marketing/campaigns/${encodeURIComponent(campaign.campaignId)}` }, filters)
  const others = campaign.competitionsPromoted - 1
  return (
    <li className="cd-campaign">
      <div className="cd-campaign__head">
        <Link to={href} className="ci-table__link cd-campaign__name">
          {campaign.name}
        </Link>
        <Badge tone={campaign.status === 'active' ? 'positive' : 'neutral'}>{wordLabel(campaign.status)}</Badge>
      </div>
      <dl className="cd-facts">
        <div>
          <dt>Dates</dt>
          <dd>{dates(campaign.startsAt, campaign.endsAt)}</dd>
        </div>
        <div>
          <dt>Objective</dt>
          <dd>{wordLabel(campaign.objective)}</dd>
        </div>
        <div>
          <dt>Also promotes</dt>
          <dd>{others === 0 ? 'No other competition' : `${others} other ${others === 1 ? 'competition' : 'competitions'}`}</dd>
        </div>
      </dl>
      {campaign.channels.length > 0 ? (
        <ul className="cd-campaign__channels" aria-label={`${campaign.name} channels`}>
          {campaign.channels.map((channel) => (
            <li key={channel.channelId}>
              <span className="cd-campaign__channel">{channel.name}</span>
              <span className="cd-muted">
                {' '}
                · {wordLabel(channel.kind)} · {wordLabel(channel.status)}
                {channel.startsAt || channel.endsAt ? ` · ${dates(channel.startsAt, channel.endsAt)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="cd-muted">No channels yet.</p>
      )}
    </li>
  )
}
